import { apiClient } from "@/services/apiClient";

export interface PendingEmail {
  sendEmailId: number;
  recipientEmail: string;
  senderEmail: string;
  copyEmail?: string;
  bccEmail?: string;
  emailSubject: string;
  emailMessage?: string;
  createDt: string;
  scheduledSendDt: string;
  guid: string;
  autoSend: number;
  autoSendApprovedBy?: string;
  autoSendApprovedDt?: string;
  createUser: string;
  addAttachment: boolean;
  attachmentPath?: string;
}

export interface UserRole {
  userRoleId: number;
  userEmail: string;
  role: string;
  grantedBy: string;
  grantedDt: string;
  effEndDt?: string;
}

export interface UserSolutionAccess {
  solutionKey: string;
  canView: boolean;
  canAdmin: boolean;
}

export interface RoleCatalogItem {
  roleCode: string;
  roleDisplayName: string;
  isAssignable: boolean;
  isSystem: boolean;
  sortOrder: number;
}

export interface SolutionCatalogItem {
  solutionKey: string;
  solutionDisplayName: string;
  routePrefix?: string;
  isEnabled: boolean;
  sortOrder: number;
}

export interface RoleSolutionAccessItem {
  roleCode: string;
  solutionKey: string;
  canView: boolean;
  canAdmin: boolean;
  lastUpdateDt?: string;
}

export interface CopyUserRolesResult {
  sourceUserEmail: string;
  targetUserEmail: string;
  copiedRoles: string[];
  skippedExistingRoles: string[];
  skippedInvalidRoles: string[];
}

export interface ReportDefinition {
  m365ReportId: number;
  reportTitle: string;
  reportSettings?: string;
  reportOptions?: string;
  guid: string;
  runAfter?: string;
  schedule?: string;
  isActive: boolean;
  isScheduled: boolean;
  lastRunDate?: string;
  lastRunStatus?: string;
}

export interface CreateReportDefinitionRequest {
  reportTitle: string;
  reportSettings?: string;
  reportOptions?: string;
  runAfter?: string;
  schedule?: string;
  isActive: boolean;
  isScheduled: boolean;
}

export interface ReportRun {
  m365ReportRunId: number;
  m365ReportId: number;
  reportTitle: string;
  reportOptions?: string;
  reportProperties?: string;
  reportStatus: string;
  reportRun: number;
  reportComplete: number;
  reportResult?: string;
  reportStartDate?: string;
  reportEndDate?: string;
  guid: string;
  createUser: string;
  createDt: string;
  generatedReportsCount: number;
}

export interface GeneratedReport {
  m365AdminReportId: number;
  m365ReportRunId: number;
  reportTitle: string;
  reportLink: string;
  reportFilename: string;
  reportRecipient?: string;
  createDt: string;
  guid: string;
}

export interface PimActivation {
  pimActivationId: number;
  uniqueId: string;
  activityDateTime: string;
  activationType?: string;
  userDisplayName?: string;
  userPrincipalName?: string;
  role?: string;
  reason?: string;
  targetResource?: string;
  startTime?: string;
  expirationTime?: string;
  correlationId?: string;
  result?: string;
  activityDisplayName?: string;
}

export interface PimAutomationGroup {
  m365AdminGroupId: number;
  title: string;
  description?: string;
  active?: string;
  eligible?: string;
  backup?: string;
  schedule?: string;
  isScheduled: boolean;
  isActive: boolean;
}

export interface PimGroupMember {
  id: string;
  displayName: string;
  email: string;
}

export interface PimAutomationDetails {
  group: PimAutomationGroup;
  activeMembers: PimGroupMember[];
  eligibleMembers: PimGroupMember[];
}

export async function getPendingEmails(): Promise<PendingEmail[]> {
  const res = await apiClient.get("/api/admin/emailapproval/pending");
  return res.data;
}

export async function getRejectedEmails(): Promise<PendingEmail[]> {
  const res = await apiClient.get("/api/admin/emailapproval/rejected");
  return res.data;
}

export async function getApprovalHistory(): Promise<PendingEmail[]> {
  const res = await apiClient.get("/api/admin/emailapproval/history");
  return res.data;
}

export async function approveEmails(emailGuids: string[]): Promise<{ approvedCount: number }> {
  const res = await apiClient.post("/api/admin/emailapproval/approve", { emailGuids });
  return res.data;
}

export async function rejectEmails(emailGuids: string[]): Promise<{ rejectedCount: number }> {
  const res = await apiClient.post("/api/admin/emailapproval/reject", { emailGuids });
  return res.data;
}

export async function getMyRoles(): Promise<UserRole[]> {
  const res = await apiClient.get("/api/admin/roles/my-roles");
  return res.data;
}

export async function getMySolutionAccess(): Promise<UserSolutionAccess[]> {
  const res = await apiClient.get("/api/admin/roles/my-solution-access");
  return res.data;
}

export async function getMyHighestRole(): Promise<{ role: string }> {
  const res = await apiClient.get("/api/admin/roles/my-highest-role");
  return res.data;
}

export async function getAllRoleAssignments(): Promise<UserRole[]> {
  const res = await apiClient.get("/api/admin/roles/all");
  return res.data;
}

export async function assignRole(userEmail: string, role: string): Promise<void> {
  await apiClient.post("/api/admin/roles/assign", { userEmail, role });
}

export async function revokeRole(userRoleId: number): Promise<void> {
  await apiClient.post("/api/admin/roles/revoke", { userRoleId });
}

export async function copyRolesFromUser(sourceUserEmail: string, targetUserEmail: string): Promise<CopyUserRolesResult> {
  const res = await apiClient.post("/api/admin/roles/copy-from-user", { sourceUserEmail, targetUserEmail });
  return res.data;
}

export async function getRoleCatalog(): Promise<RoleCatalogItem[]> {
  const res = await apiClient.get("/api/admin/roles/catalog/roles");
  return res.data;
}

export async function getSolutionCatalog(): Promise<SolutionCatalogItem[]> {
  const res = await apiClient.get("/api/admin/roles/catalog/solutions");
  return res.data;
}

export async function getRoleSolutionAccessCatalog(): Promise<RoleSolutionAccessItem[]> {
  const res = await apiClient.get("/api/admin/roles/catalog/access");
  return res.data;
}

export async function upsertRoleSolutionAccess(payload: RoleSolutionAccessItem): Promise<void> {
  await apiClient.put("/api/admin/roles/catalog/access", {
    roleCode: payload.roleCode,
    solutionKey: payload.solutionKey,
    canView: payload.canView,
    canAdmin: payload.canAdmin,
    expectedLastUpdateDt: payload.lastUpdateDt,
  });
}

// Reports API
export async function getReportDefinitions(): Promise<ReportDefinition[]> {
  const res = await apiClient.get("/api/admin/reports");
  return res.data;
}

export async function getReportRuns(m365ReportId?: number): Promise<ReportRun[]> {
  const params = m365ReportId ? { m365ReportId } : {};
  const res = await apiClient.get("/api/admin/reports/runs", { params });
  return res.data;
}

export async function getReportRunsBySiteUrl(siteUrl: string): Promise<ReportRun[]> {
  const res = await apiClient.get("/api/admin/reports/runs/by-site-url", { params: { siteUrl } });
  return res.data;
}

export async function getGeneratedReports(m365ReportRunId: number, siteUrl?: string): Promise<GeneratedReport[]> {
  const params = siteUrl ? { siteUrl } : {};
  const res = await apiClient.get(`/api/admin/reports/runs/${m365ReportRunId}/generated`, { params });
  return res.data;
}

export async function createReportDefinition(request: CreateReportDefinitionRequest): Promise<{ m365ReportId: number }> {
  const res = await apiClient.post("/api/admin/reports", request);
  return res.data;
}

export async function triggerReport(m365ReportId: number, reportOptions?: string, reportProperties?: string): Promise<{ m365ReportRunId: number }> {
  const res = await apiClient.post("/api/admin/reports/trigger", { m365ReportId, reportOptions, reportProperties });
  return res.data;
}

export async function reRunReport(m365ReportRunId: number): Promise<{ m365ReportRunId: number }> {
  const res = await apiClient.post("/api/admin/reports/rerun", { m365ReportRunId });
  return res.data;
}

export async function getPimActivations(): Promise<PimActivation[]> {
  const res = await apiClient.get("/api/admin/pim/activations");
  return res.data;
}

export async function getPimAutomations(): Promise<PimAutomationGroup[]> {
  const res = await apiClient.get("/api/admin/pim/automations");
  return res.data;
}

export async function getPimAutomationDetails(m365AdminGroupId: number): Promise<PimAutomationDetails> {
  const res = await apiClient.get(`/api/admin/pim/automations/${m365AdminGroupId}`);
  return res.data;
}

export async function addEligibleMember(m365AdminGroupId: number, userId: string): Promise<void> {
  await apiClient.post(`/api/admin/pim/automations/${m365AdminGroupId}/eligible-members`, { userId });
}

export async function removeEligibleMember(m365AdminGroupId: number, userId: string): Promise<void> {
  await apiClient.delete(`/api/admin/pim/automations/${m365AdminGroupId}/eligible-members/${encodeURIComponent(userId)}`);
}

// Review Templates
export interface ReviewTemplateSummary {
  templateGuid: string;
  templateName: string;
  category: string;
  description: string;
  isActive: boolean;
  totalSteps: number;
  requiredSteps: number;
  reviewTemplateConfig?: string;
}

export async function getTemplates(includeInactive = false): Promise<ReviewTemplateSummary[]> {
  const res = await apiClient.get<ReviewTemplateSummary[]>(
    `/api/admin/reviewmanagement/templates?includeInactive=${includeInactive}`
  );
  return res.data;
}

